import { useRef, useState } from 'react';
import { DateTime } from 'luxon';
import { tenantControllerGetTenantSalesCsv } from '@/generated/korza-api-client';
import { useTenant } from '@/hooks/use-tenant';
import { useI18nContext } from '@/i18n/i18n-react';
import { toast } from '@/components/ui/sonner';
import {
  buildSalesCsvQuery,
  type SalesHistoryFilterValues,
} from '@/lib/sales-history';

/**
 * CSV export of the sales history with the current filters. A failure shows
 * an error toast (SALES-09); a click while a download runs is ignored.
 */
export function useSalesCsv(values: SalesHistoryFilterValues): {
  download(): Promise<void>;
  isLoading: boolean;
} {
  const { LL, locale } = useI18nContext();
  const tenantSlug = useTenant().slug;
  const [isLoading, setIsLoading] = useState(false);
  // The state only updates on the next render; the ref stops a double click.
  const isRunning = useRef(false);

  const download = async () => {
    if (isRunning.current) return;
    isRunning.current = true;
    setIsLoading(true);
    try {
      const response = await tenantControllerGetTenantSalesCsv({
        path: { tenantSlug },
        query: buildSalesCsvQuery(values, locale),
        parseAs: 'blob',
        throwOnError: true,
      });

      const blob = response.data as Blob;
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `korza-${locale === 'pt' ? 'vendas' : 'sales'}-${tenantSlug}-${DateTime.now().startOf('second').toISO({ includeOffset: false, suppressMilliseconds: true })}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch {
      toast.error(LL.salesPage.csvError());
    } finally {
      isRunning.current = false;
      setIsLoading(false);
    }
  };

  return { download, isLoading };
}
